import Localiser from '../../../../core/modules/Localiser';
import getSite from '../../../../core/modules/sites/getSite';
import { AddDynamicWidgets } from '../../../../core/types/AddDynamicWidgets';
import { GetPlatformData } from '../../../../core/types/GetPlatformData';
import { getJSON } from '../../../../sharedModules/api/apiUtils';
import { WIDGET_PLACEHOLDER_SELECTOR } from '../../../../utils/constants';
import getUrlParameters from '../../../../utils/getUrlParameters';
import { getWidgetsAssetsEndpoint } from '../../../../utils/getWidgetsAssetsEndpoint';
import { updateHawk } from '../../../../utils/global';
import { getNodes } from '../../../utils/getNodes';

import { getLanguage } from './getLanguage';
import { handleInfiniteScrollArticles } from './handleInfiniteScrollArticles';
import { processWidget } from './processWidget';
import { resolveWidgets } from './resolveWidgets';

/**
 *  Editorial widget (has render type "editorial" OR empty) AND is not one of [link, inbody, deal]
 */
const hasEditorialWidgets = (widgetPlaceholders: HTMLElement[]): boolean => {
  return widgetPlaceholders.some((placeholder) => {
    const renderType = placeholder.getAttribute('data-render-type');
    const widgetType = placeholder.getAttribute('data-widget-type');
    return (
      (!renderType || renderType === 'editorial') &&
      !['link', 'inbody', 'deal'].includes(widgetType ?? '')
    );
  });
};

export const processWidgets = async ({
  getPlatformData,
  addDynamicWidgets,
}: {
  getPlatformData?: GetPlatformData;
  addDynamicWidgets?: AddDynamicWidgets;
}): Promise<void> => {
  const detail = { script: 'HAWK' };
  performance.mark('Hawk widgets processing started', { detail });

  /* Set the translations in window.hawk here instead of in ecomwidgets.ts,
  so they are available in entry scripts
  E.g., setting widget title override on WHATHIFI */
  const language = getLanguage();
  const platformData = getPlatformData ? await getPlatformData() : {};
  const site = getSite({
    href: document.location.href,
    keywords: platformData.keywords,
    requestedSite: getUrlParameters().site,
  });
  const unprocessedWidgets = getNodes({
    selector: WIDGET_PLACEHOLDER_SELECTOR,
  });
  const editorialOnPage = hasEditorialWidgets(unprocessedWidgets);

  const localiser = new Localiser(language, platformData.territory);
  await localiser.load(getJSON);

  /* Set the webpack public path dynamically - this needs to be different depending on
  where the script is loaded
  E.g., stage, live, local
  The script is loaded via CDN and is not on the same url as the site
  Must add a slash after the endpoint for chunks */
  __webpack_public_path__ = `${getWidgetsAssetsEndpoint()}/`;

  updateHawk({
    version: process.env.VERSION,
    processWidget,
    articleType: platformData.articleType,
    keywords: platformData.keywords,
    territory: platformData.territory,
    site,
    localiser,
    articleName: document.title,
    articleUrl: document.location.href,
    editorialOnPage,
  });

  const unprocessedDynamicWidgets = addDynamicWidgets
    ? await addDynamicWidgets({
        articleName: document.title,
        articleUrl: document.location.href,
        editorialOnPage,
        localiser,
        platformData,
        site,
      })
    : [];

  await resolveWidgets({
    unprocessedWidgets: unprocessedWidgets.concat(unprocessedDynamicWidgets),
    platformData,
    site,
    localiser,
    editorialOnPage,
  });

  performance.mark('Hawk widgets processing completed', { detail });
  performance.measure('Hawk widgets processing span', {
    start: 'Hawk widgets processing started',
    end: 'Hawk widgets processing completed',
    detail,
  });

  handleInfiniteScrollArticles({
    site,
    localiser,
    editorialOnPage,
    platformData,
  });
};
